import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Check, ChevronRight, ExternalLink } from "lucide-react";
import AppFooter from "../components/AppFooter";

const TEAL = "#b8d4d0";
const ACCENT = "#DDFECE";
const BG = "var(--pord-bg, #222518)";

export default function ApiDocsPage() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(null);
  const baseUrl = process.env.REACT_APP_BACKEND_URL || "";

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2500);
  };

  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: "'DM Sans', sans-serif", color: "rgba(255,255,255,0.82)" }}>
      {/* Header */}
      <div style={{ padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(184,212,208,0.06)" }}>
        <h1 style={{ fontSize: 20, fontWeight: 400, color: "#fff", margin: 0, letterSpacing: "0.02em" }}>
          Pord API <span style={{ color: TEAL, fontWeight: 300 }}>Integration Guide</span>
        </h1>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: `1px solid rgba(184,212,208,0.2)`, borderRadius: 999, padding: "6px 18px", color: TEAL, fontSize: 12, cursor: "pointer" }}>
          Back
        </button>
      </div>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "32px 24px 60px" }}>

        {/* 1. Overview */}
        <Section num="1" title="Overview">
          <p>The <strong>Pord</strong> API provides weekly processed CoT/TFF positioning data for supported futures contracts. Data is sourced from CFTC Commitments of Traders reports, enriched with derived analytics including percentile rankings, flow sentiment, and participant analysis.</p>
          <p>Data is intended for internal research, dashboards, and model ingestion.</p>
          <InfoBox>
            <strong>Dataset:</strong> V3 schema with 177 fields per contract per week<br />
            <strong>Coverage:</strong> 95+ futures contracts across Money Market, FX, Equity Index, Energy, Agriculture, and Metals<br />
            <strong>History:</strong> Weekly data from 2016 to present<br />
            <strong>Update cadence:</strong> Weekly, published after CFTC release and internal validation
          </InfoBox>
        </Section>

        {/* 2. Authentication */}
        <Section num="2" title="Authentication">
          <p>All API requests require an API key. Pass your key in the request header:</p>
          <CodeBlock id="auth1" label="Python" code={`import requests

API_KEY = "pord_live_your_api_key_here"
HEADERS = {"Authorization": f"Bearer {API_KEY}"}`} onCopy={copy} copied={copied} />
          <InfoBox>
            API keys use the prefix <code style={{ color: ACCENT }}>pord_live_</code> followed by a 48-character hex string. Keys are shown once at creation — store securely. Contact your Pord account manager to obtain keys.
          </InfoBox>
        </Section>

        {/* 3. Endpoints */}
        <Section num="3" title="Endpoints">
          <Endpoint method="GET" path="/v1/status" desc="API health and latest dataset date.">
            <CodeBlock id="status" label="Python" code={`response = requests.get(
    "${baseUrl}/api/v1/status",
    headers=HEADERS
)
print(response.json())`} onCopy={copy} copied={copied} />
            <CodeBlock id="status-resp" label="Example Response" code={`{
  "data": {
    "api_version": "v1",
    "dataset_version": "V3",
    "latest_report_date": "2026-07-21",
    "total_contracts": 95,
    "total_records": 28951
  },
  "meta": {
    "status": "healthy",
    "timestamp": "2026-07-27T03:00:00Z"
  }
}`} onCopy={copy} copied={copied} />
          </Endpoint>

          <Endpoint method="GET" path="/v1/contracts" desc="List all supported contracts with metadata.">
            <CodeBlock id="contracts" label="Python" code={`contracts = requests.get(
    "${baseUrl}/api/v1/contracts",
    headers=HEADERS
).json()
print(f"Total contracts: {contracts['meta']['count']}")`} onCopy={copy} copied={copied} />
          </Endpoint>

          <Endpoint method="GET" path="/v1/weekly-summary" desc="Latest weekly cross-section for all contracts.">
            <div style={{ fontSize: 13, marginBottom: 12, color: "rgba(255,255,255,0.55)" }}>
              <strong>Parameters:</strong> <code>report_date</code> (YYYY-MM-DD, optional), <code>contract_code</code> (optional)
            </div>
            <CodeBlock id="summary" label="Python — Single Contract" code={`summary = requests.get(
    "${baseUrl}/api/v1/weekly-summary",
    params={"contract_code": "13874A"},
    headers=HEADERS
).json()
for row in summary["data"]:
    print(f"{row['contract_name']}: OI={row['open_interest']}")`} onCopy={copy} copied={copied} />
            <CodeBlock id="summary-resp" label="Example Response" code={`{
  "data": [
    {
      "contract_code": "13874A",
      "contract_name": "E-MINI S&P 500",
      "report_date": "2026-07-21",
      "open_interest": 2785508,
      "oi_change": 84232,
      "oi_pct_change": 3.1,
      "dealer_net": 156789,
      "dealer_net_pctoi": 5.63,
      "dealer_pctile": 72.5,
      "asset_mgr_net": -234567,
      "asset_mgr_net_pctoi": -8.42,
      "asset_mgr_pctile": 15.3,
      "flow_sentiment_wow": "Mix_SkewedDOWN",
      "flow_sentiment_6m": "Both DOWN"
    }
  ],
  "meta": {
    "report_date": "2026-07-21",
    "count": 1,
    "dataset_version": "V3"
  }
}`} onCopy={copy} copied={copied} />
          </Endpoint>

          <Endpoint method="GET" path="/v1/history" desc="Historical weekly series for a single contract.">
            <div style={{ fontSize: 13, marginBottom: 12, color: "rgba(255,255,255,0.55)" }}>
              <strong>Parameters:</strong> <code>contract_code</code> (required), <code>start_date</code>, <code>end_date</code>, <code>fields</code> (comma-separated), <code>limit</code> (1–1000, default 260)
            </div>
            <CodeBlock id="history" label="Python — Last 52 Weeks" code={`history = requests.get(
    "${baseUrl}/api/v1/history",
    params={"contract_code": "134741", "limit": 52},
    headers=HEADERS
).json()
print(f"SOFR-3M: {history['meta']['count']} weeks")`} onCopy={copy} copied={copied} />
          </Endpoint>

          <Endpoint method="GET" path="/v1/openapi.json" desc="Machine-readable OpenAPI 3.0 specification.">
            <CodeBlock id="openapi" label="Python" code={`spec = requests.get(
    "${baseUrl}/api/v1/openapi.json",
    headers=HEADERS
).json()
print(spec["info"]["title"])`} onCopy={copy} copied={copied} />
          </Endpoint>
        </Section>

        {/* 4. Weekly Pull Workflow */}
        <Section num="4" title="Weekly Pull Workflow">
          <ol style={{ paddingLeft: 20, lineHeight: 2.2, fontSize: 14 }}>
            <li>Call <code>/v1/status</code> to check the latest published <code>latest_report_date</code>.</li>
            <li>If there is a new week, call <code>/v1/weekly-summary</code> for the full cross-section.</li>
            <li>Optionally call <code>/v1/history</code> for specific contracts you track.</li>
            <li>Store the returned snapshot internally. Log <code>dataset_version</code> and response timestamp for auditability.</li>
            <li>Repeat weekly after CFTC publishes (typically Friday evening ET).</li>
          </ol>
          <CodeBlock id="workflow" label="Python Example — Weekly Pull" code={`import requests

API_KEY = "pord_live_your_key_here"
BASE = "${baseUrl}/api"
HEADERS = {"Authorization": f"Bearer {API_KEY}"}

# Step 1: Check latest date
status = requests.get(f"{BASE}/v1/status", headers=HEADERS).json()
latest = status["data"]["latest_report_date"]
print(f"Latest report: {latest}")

# Step 2: Pull weekly summary
summary = requests.get(
    f"{BASE}/v1/weekly-summary",
    headers=HEADERS
).json()
print(f"Contracts: {summary['meta']['count']}")

# Step 3: Pull history for a specific contract
history = requests.get(
    f"{BASE}/v1/history",
    params={"contract_code": "134741", "limit": 52},
    headers=HEADERS
).json()
print(f"SOFR-3M history: {history['meta']['count']} weeks")`} onCopy={copy} copied={copied} />
        </Section>

        {/* 5. Rate Limits */}
        <Section num="5" title="Rate Limits">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(184,212,208,0.12)" }}>
                <th style={thSt}>Limit</th><th style={thSt}>Value</th><th style={thSt}>Scope</th>
              </tr>
            </thead>
            <tbody>
              <tr style={trSt}><td style={tdSt}>Daily calls</td><td style={tdSt}>100 / day</td><td style={tdSt}>Per API key, resets at midnight UTC</td></tr>
              <tr style={trSt}><td style={tdSt}>Burst rate</td><td style={tdSt}>10 / minute</td><td style={tdSt}>Per API key</td></tr>
            </tbody>
          </table>
          <InfoBox>
            When rate limited, the API returns <code>429 Too Many Requests</code>. Implement exponential backoff. Contact your account manager for higher limits.
          </InfoBox>
        </Section>

        {/* 6. Error Handling */}
        <Section num="6" title="Error Handling">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(184,212,208,0.12)" }}>
                <th style={thSt}>Code</th><th style={thSt}>Meaning</th><th style={thSt}>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr style={trSt}><td style={tdSt}><code>401</code></td><td style={tdSt}>Invalid or missing API key</td><td style={tdSt}>Check your key is correct and active</td></tr>
              <tr style={trSt}><td style={tdSt}><code>403</code></td><td style={tdSt}>Key revoked or expired</td><td style={tdSt}>Contact account manager for a new key</td></tr>
              <tr style={trSt}><td style={tdSt}><code>404</code></td><td style={tdSt}>Unknown contract or unavailable date</td><td style={tdSt}>Verify contract_code via /v1/contracts</td></tr>
              <tr style={trSt}><td style={tdSt}><code>429</code></td><td style={tdSt}>Rate limit exceeded</td><td style={tdSt}>Wait and retry with backoff</td></tr>
              <tr style={trSt}><td style={tdSt}><code>503</code></td><td style={tdSt}>Data loading / temporary unavailable</td><td style={tdSt}>Retry after 10 seconds</td></tr>
            </tbody>
          </table>
        </Section>

        {/* 7. Versioning & Revision Policy */}
        <Section num="7" title="Versioning & Revision Policy">
          <ul style={{ paddingLeft: 20, lineHeight: 2, fontSize: 14 }}>
            <li>Base path is <code>/v1/</code>. All current endpoints use this version.</li>
            <li>New fields may be <strong>added</strong> to responses without a version bump — ensure your parser ignores unknown fields.</li>
            <li>Field removals or semantic changes only occur in a new major version (<code>/v2/</code>).</li>
            <li>Weekly data is published after internal validation. Historical revisions, if any, are rare and communicated via <code>dataset_version</code>.</li>
          </ul>
        </Section>

        {/* 8. Field Dictionary */}
        <Section num="8" title="Field Dictionary — Key Fields">
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 14 }}>Fields are organized into three families: Source (CFTC-reported), Derived (computed), and Interpretation (proprietary signals).</p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(184,212,208,0.12)" }}>
                <th style={thSt}>Field</th><th style={thSt}>Type</th><th style={thSt}>Family</th><th style={thSt}>Description</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["contract_code", "string", "Source", "CFTC contract market code"],
                ["report_date", "date", "Source", "CFTC report date (Tuesday of report week)"],
                ["open_interest", "integer", "Source", "Total open interest"],
                ["dealer_net", "integer", "Derived", "Dealer net position (Long − Short)"],
                ["asset_mgr_net_pctoi", "number", "Derived", "Asset Manager net as % of OI"],
                ["dealer_pctile", "number", "Derived", "52-week PERCENTRANK.INC of Dealer NET %OI"],
                ["dealer_gross", "integer", "Derived", "Dealer gross contracts (L + S + Spread)"],
                ["flow_sentiment_wow", "string", "Interpretation", "Week-over-week flow direction: Both UP, Both DOWN, Mix_SkewedUP, Mix_SkewedDOWN"],
                ["flow_sentiment_6m", "string", "Interpretation", "6-month flow direction based on slope analysis"],
              ].map(([f, t, fam, d]) => (
                <tr key={f} style={trSt}>
                  <td style={{ ...tdSt, color: ACCENT }}><code>{f}</code></td>
                  <td style={tdSt}>{t}</td>
                  <td style={{ ...tdSt, color: fam === "Source" ? TEAL : fam === "Derived" ? "rgba(255,255,255,0.7)" : "#DDFECE" }}>{fam}</td>
                  <td style={tdSt}>{d}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 12 }}>Full field dictionary available via the <code>/v1/openapi.json</code> endpoint.</p>
        </Section>

        {/* 9. Data Use & Caching */}
        <Section num="9" title="Data Use & Internal Caching">
          <p style={{ marginBottom: 12 }}>When you access data from Pord (including via the portal, CSV export, or API), you are granted a limited right to:</p>
          <ul style={{ paddingLeft: 20, lineHeight: 2, fontSize: 14, marginBottom: 16 }}>
            <li>Store and cache the data within your own internal systems, databases, research tools, and archives.</li>
            <li>Use the data in your internal models, dashboards, risk tools, and research or investment workflows.</li>
            <li>Retain historical copies of the data for your own internal records, compliance, audit, and research purposes.</li>
          </ul>
          <p style={{ marginBottom: 12 }}><strong style={{ color: "#fff" }}>Restrictions:</strong></p>
          <ul style={{ paddingLeft: 20, lineHeight: 2, fontSize: 14, marginBottom: 16 }}>
            <li>You may not sell, license, sublicense, distribute, or otherwise provide Pord data as a standalone product, data feed, or service to any third party.</li>
            <li>You may not make Pord data available to your own clients or any external party in a way that allows them to access, download, or systematically extract the underlying dataset.</li>
            <li>You may not use Pord data to power any external-facing product, service, dashboard, or report whose primary purpose is to expose Pord's dataset.</li>
            <li>You may incorporate Pord data into your own internal analyses, reports, and materials (including those you share with clients), provided that such materials do not enable recipients to access or reconstruct Pord's dataset itself.</li>
          </ul>
          <InfoBox>
            <strong>Post-Termination:</strong> If your subscription ends, you must stop accessing new data. You may continue to store and use historical data already obtained solely for internal archive, compliance, audit, and research purposes, subject to all limitations above.
          </InfoBox>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", marginTop: 12 }}>Full terms available at <a href="/terms" style={{ color: TEAL }}>/terms</a></p>
        </Section>

        {/* 10. Support */}
        <Section num="10" title="Support & Contact">
          <p>For API key requests, rate limit increases, or technical support:</p>
          <InfoBox>
            <strong>Email:</strong> annie@clarityjournalbyenquips.com<br />
            <strong>Documentation:</strong> This page + <code>/v1/openapi.json</code><br />
            <strong>Status:</strong> Check <code>/v1/status</code> for real-time API health
          </InfoBox>
        </Section>

      </div>
      <AppFooter />
    </div>
  );
}

/* ─── Reusable components ─────────────────────────────────── */

function Section({ num, title, children }) {
  return (
    <section style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 18, fontWeight: 400, color: "#fff", marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ color: TEAL, fontSize: 14, fontWeight: 300 }}>{num}.</span>
        {title}
      </h2>
      <div style={{ fontSize: 14, lineHeight: 1.8 }}>{children}</div>
    </section>
  );
}

function Endpoint({ method, path, desc, children }) {
  return (
    <div style={{ marginBottom: 24, padding: "16px 20px", background: "rgba(255,255,255,0.02)", borderRadius: 8, border: "1px solid rgba(184,212,208,0.08)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ background: "rgba(184,212,208,0.12)", padding: "2px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600, color: ACCENT, letterSpacing: "0.06em" }}>{method}</span>
        <code style={{ color: "#fff", fontSize: 14 }}>{path}</code>
      </div>
      <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", margin: "0 0 12px" }}>{desc}</p>
      {children}
    </div>
  );
}

function CodeBlock({ id, label, code, onCopy, copied }) {
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 4, letterSpacing: "0.04em" }}>{label}</div>}
      <div style={{ position: "relative", background: "rgba(0,0,0,0.25)", borderRadius: 6, border: "1px solid rgba(184,212,208,0.06)" }}>
        <pre style={{ margin: 0, padding: "12px 14px", fontSize: 12, color: TEAL, overflow: "auto", fontFamily: "'JetBrains Mono', 'Fira Code', monospace", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>{code}</pre>
        <button onClick={() => onCopy(code, id)}
          style={{ position: "absolute", top: 8, right: 8, background: "rgba(184,212,208,0.08)", border: "1px solid rgba(184,212,208,0.15)", borderRadius: 4, padding: "4px 8px", cursor: "pointer", color: TEAL, fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
          {copied === id ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
        </button>
      </div>
    </div>
  );
}

function InfoBox({ children }) {
  return (
    <div style={{ margin: "12px 0", padding: "14px 18px", background: "rgba(184,212,208,0.04)", borderRadius: 8, border: "1px solid rgba(184,212,208,0.10)", fontSize: 13, lineHeight: 1.8, color: "rgba(255,255,255,0.7)" }}>
      {children}
    </div>
  );
}

const thSt = { textAlign: "left", padding: "8px 10px", color: TEAL, fontSize: 12, fontWeight: 400, letterSpacing: "0.04em" };
const tdSt = { padding: "8px 10px", color: "rgba(255,255,255,0.7)", borderBottom: "1px solid rgba(184,212,208,0.04)" };
const trSt = {};
